// Storage Safety & Auto-Pruning Initializer
// Runs before all modules to ensure localStorage never overflows or crashes React

export function initStorageSafety() {
  if (typeof window === 'undefined') return;

  try {
    // 1. Wrap Storage.prototype.setItem early to prevent any QuotaExceeded crashes
    if (window.Storage && Storage.prototype.setItem) {
      const originalStorageSetItem = Storage.prototype.setItem;
      Storage.prototype.setItem = function (key: string, value: string) {
        try {
          originalStorageSetItem.call(this, key, value);
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          console.warn(`[Storage Safety] Storage.setItem skipped for key "${key}" (quota reached):`, msg);
        }
      };
    }

    // 2. Prune legacy bloated keys to immediately free up browser quota
    if (window.localStorage) {
      const essentialKeys = new Set([
        'gantt_dark_mode',
        'gantt_active_workspace',
        'gantt_current_project_id',
        'gwantt_categories_professional',
        'gwantt_categories_private',
        'gantt_kanban_mode',
        'gantt_show_dependencies',
        'gantt_date_padding',
        'gantt_hide_empty_days',
        'gantt_show_completed',
        'gantt_show_archived',
        'gantt_show_task_details',
        'gantt_search_mode',
        'gantt_filter_assignee',
        'gantt_filter_type',
        'gantt_filter_status',
        'gantt_filter_priority',
        'stratum_tour_completed',
        'stratum_dragdrop_hint_seen',
        'gwantt_report_organisation',
        'gwantt_report_abteilung',
        'gwantt_report_sachbearbeiter',
        'gwantt_report_telefon',
        'gwantt_report_fax',
        'gwantt_report_email',
        'gwantt_report_recipient',
        'gwantt_report_title',
        'gwantt_report_subtitle',
        'gwantt_report_intro',
        'gwantt_report_narrative_length',
        'gwantt_idea_themes',
        'gwantt_idea_types',
        'gwantt_idea_sources'
      ]);

      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;

        // Keep Firebase Auth token (essential for user login session)
        if (key.startsWith('firebase:authUser:')) continue;

        // If report logo is oversized (> 20KB), prune it to prevent quota issues; preserve legitimate small logos
        if (key === 'gwantt_report_logo') {
          const logoVal = localStorage.getItem(key);
          if (logoVal && logoVal.length > 20480) {
            keysToRemove.push(key);
          }
          continue;
        }

        // Identify bloated or legacy caches
        if (
          key.startsWith('firestore_') || // Clean up bloated multi-tab mutation/target keys dumped into localStorage
          key.startsWith('gwantt_global_metadata_') ||
          key.startsWith('gwantt_offline_queue') ||
          key.startsWith('gantt_projects') ||
          key.startsWith('gantt_tasks') ||
          key.startsWith('gantt_backup') ||
          key.startsWith('gwantt_backup') ||
          key.startsWith('stratum_backup') ||
          key.startsWith('gwantt_last_view_') ||
          key.startsWith('mindmap_') ||
          key === 'gantt_state' ||
          key === 'gantt_full_dump'
        ) {
          keysToRemove.push(key);
          continue;
        }

        // If any non-essential item exceeds 15KB, remove it to preserve storage quota
        try {
          const val = localStorage.getItem(key);
          if (val && val.length > 15360 && !essentialKeys.has(key)) {
            keysToRemove.push(key);
          }
        } catch (e) { /* ignore */ }
      }

      if (keysToRemove.length > 0) {
        keysToRemove.forEach(k => {
          try { localStorage.removeItem(k); } catch (e) { /* ignore */ }
        });
      }
    }
  } catch (e) {
    console.warn('[Storage Safety] Storage initialization notice:', e);
  }
}

// Auto-execute on import
initStorageSafety();
